import clsx from 'clsx';
import type { ReactNode } from 'react';
import { formatInt } from '@/lib/format';

export type BarListItem = {
  key: string;
  count: number;
  /** Optional custom label (e.g. a link). Falls back to key. */
  label?: ReactNode;
};

export type BarListProps = {
  data: BarListItem[];
  total?: number;
  emptyLabel?: string;
  /** Keep the given order. The default sorts by count. */
  preserveOrder?: boolean;
};

function displayKey(key: string) {
  if (key === '') return '(unknown)';
  if (key === 'other') return 'Other';
  return key;
}

export function BarList({
  data,
  total: totalProp,
  emptyLabel = 'No data',
  preserveOrder = false,
}: BarListProps) {
  const total = totalProp ?? data.reduce((s, d) => s + d.count, 0);
  if (data.length === 0 || total === 0) {
    return <p className="text-sm text-gray-500">{emptyLabel}</p>;
  }
  const sorted = preserveOrder ? data : [...data].sort((a, b) => b.count - a.count);
  return (
    <ul className="space-y-2">
      {sorted.map((row) => {
        const pct = Math.round((row.count / total) * 100);
        return (
          <li key={row.key || '__empty__'}>
            <div className="mb-0.5 flex items-center justify-between gap-2 text-xs text-gray-600">
              <span className="min-w-0 flex-1">
                {row.label == null || typeof row.label === 'string' ? (
                  <span className="block truncate">{row.label ?? displayKey(row.key)}</span>
                ) : (
                  row.label
                )}
              </span>
              <span className="shrink-0 font-medium text-gray-800">{formatInt(row.count)}</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-gray-100">
              <div
                className={clsx('h-full rounded-full bg-primary transition-all')}
                style={{ width: `${pct}%` }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}
